
import { Valet } from './icons/Valet'
import { Calendar } from './icons/Calendar'
import { Lightning } from './icons/Lightning'
import { Revert } from './icons/Revert'
import { Tbank } from './icons/Tbank'
import { Sber } from './icons/Sber'
import { cn } from '~shared/utils/cn'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'
import { Button } from '~shared/components/button-v2'
import { LeadFormDialog } from '~widgets/lead-form/components/lead-form-dialog'
import React from 'react'
import { calculatePrice } from '~shared/utils/calculatePrice'
import { useCalculateClosestEnrollment } from '~shared/hooks/useCalculateClosestEnrollment'
import { useCalculateStudyDuration } from '~shared/hooks/useCalculateStudyDuration'
import { insertThousandsSeparator } from '~shared/utils/insert-thousands-separator'
import { getEffectiveDiscount } from '~shared/utils/discount'

export const CostSingleSection = ({
  program
}: {
  program: Awaited<ReturnType<typeof getProgramData>>
}) => {
  // Определяем доступный формат (берём первый непустой)
  const activeFormat = [
    {
      studyForm: 'online' as const,
      title: 'Заочная форма',
      desc: 'Дистанционно',
      price: program?.costOnline?.priceOnline,
      priceOneTime: program?.costOnline?.priceOnlineOneTime,
      discount: program?.costOnline?.discountOnline,
      discountMonths: program?.costOnline?.discountMonthsOnline,
      studyDurationYears: program?.costOnline?.studyDurationYearsOnline,
      studyDurationMonths: program?.costOnline?.studyDurationMonthsOnline,
      closestEnrollment: program?.costOnline?.closestEnrollmentOnline
    },
    {
      studyForm: 'mixed' as const,
      title: 'Очно-заочная форма',
      desc: 'С применением ДОТ*',
      price: program?.cost?.price,
      priceOneTime: program?.cost?.priceOneTime,
      discount: program?.cost?.discount,
      discountMonths: program?.cost?.discountMonths,
      studyDurationYears: program?.cost?.studyDurationYears,
      studyDurationMonths: program?.cost?.studyDurationMonths,
      closestEnrollment: program?.cost?.closestEnrollment
    },
    {
      studyForm: 'offline' as const,
      title: 'Очная форма',
      desc: 'С применением ДОТ*',
      price: program?.costOffline?.priceOffline,
      priceOneTime: program?.costOffline?.priceOfflineOneTime,
      discount: program?.costOffline?.discountOffline,
      discountMonths: program?.costOffline?.discountMonthsOffline,
      studyDurationYears: program?.costOffline?.studyDurationYearsOffline,
      studyDurationMonths: program?.costOffline?.studyDurationMonthsOffline,
      closestEnrollment: program?.costOffline?.closestEnrollmentOffline
    }
  ].find(format => format.price !== null && format.price !== undefined)

  const calculatedClosestEnrollment = useCalculateClosestEnrollment({
    timestamp: activeFormat?.closestEnrollment
  })
  const calculatedStudyDuration = useCalculateStudyDuration({
    years: activeFormat?.studyDurationYears,
    months: activeFormat?.studyDurationMonths
  })
  const [isOpen, setIsOpen] = React.useState(false)

  // Если нет активного формата, возвращаем null или fallback
  if (!activeFormat) return null

  const effectiveDiscount =
    getEffectiveDiscount({
      formatDiscount: activeFormat.discount,
      category: program?.category,
      faculty: program?.faculty
    }) || 0

  const calculatedPrice = calculatePrice({
    price: activeFormat.price || 0,
    discount: effectiveDiscount,
    discountMonths: activeFormat.discountMonths || 12,
    locale: 'ru',
    semestersNumber: 2
  })

  const whatIncludes = [
    {
      id: 1,
      title: 'Рассрочка от\u00A0Т\u2011Банка',
      icon: <Tbank />
    },
    {
      id: 2,
      title: 'Образовательный кредит от Сбера',
      icon: <Sber />
    },
    {
      id: 3,
      title: calculatedStudyDuration,
      description: 'Длительность обучения',
      icon: <Calendar />
    },
    {
      id: 4,
      title: calculatedClosestEnrollment,
      description: 'Ближайшее зачисление',
      icon: <Lightning />
    },
    {
      id: 5,
      title: 'Налоговый вычет',
      description: 'Поможем вернуть 13%',
      icon: <Valet />
    },
    {
      id: 6,
      title: 'Полный возврат',
      description: 'В течение первых двух недель, если передумаете',
      icon: <Revert />
    }
  ]
  return (
    <section className='bg-black py-[80px] text-white'>
      <div
        className={cn(
          'mx-auto px-[16px] box-border',
          'max-w-[672px]',
          'md:max-w-[832px]',
          '2lg:max-w-[1280px]'
        )}>
        <h2
          className={cn(
            'mb-[24px] text-[40px] font-medium',
            'md:text-[48px] md:mb-[32px]',
            '2lg:mb-[56px] 2lg:text-[64px]'
          )}>
          Стоимость обучения
        </h2>
        <div
          className={cn(
            'bg-[#18191A] p-[16px] gap-[48px] rounded-[16px] justify-between',
            'md:p-[32px]',
            '2lg:rounded-[32px]'
          )}>
          <div
            className={cn(
              'flex gap-[16px] items-center mb-[24px] flex-wrap',
              'md:mb-[32px]',
              '2lg:mb-[46px]'
            )}>
            <span className='h-[35px] bg-[#2A2A2A] rounded-[55px] px-[16px] flex items-center'>
              {program?.category?.name}
            </span>
            {/* <p className='bg-[#702323] text-[14px] font-medium h-[35px] rounded-[55px] px-[16px] flex items-center w-fit'>
              {activeFormat.discount
                ? `Скидка ${activeFormat.discount}%`
                : 'Скидка 10% при оплате всей суммы сразу'}
            </p> */}
          </div>

          <div className={cn('2lg:grid flex flex-col grid-cols-2 gap-[16px]')}>
            {/* блок цен */}
            <div className='bg-[#F6F6F6] rounded-[24px] md:rounded-[32px] p-[24px] flex flex-col'>
              <p className={cn('text-[24px] text-black font-medium mb-[8px]')}>
                {activeFormat.title}
              </p>
              <p className={cn('text-[16px] text-[#6A6A6A]')}>
                {activeFormat.desc}
              </p>
              <p className={cn('font-medium text-black mt-[40px] text-[20px]')}>
                При рассрочке на {activeFormat.discountMonths || 12} месяцев
              </p>
              <p
                className={cn(
                  'font-medium text-black text-[32px] my-[8px]',
                  'md:text-[48px]',
                  'lg:text-[80px]'
                )}>
                {insertThousandsSeparator(
                  calculatedPrice?.yearInstallmentPlan?.price
                )}
                <span className={cn('md:text-[32px] lg:text-[48px]')}>
                  {' '}
                  ₽/мес
                </span>
              </p>
              <p
                className={cn(
                  'text-[14px] font-medium text-black/60 mb-[16px] md:mb-[24px] 2lg:text-[16px]'
                )}>
                или {insertThousandsSeparator(activeFormat.priceOneTime ?? calculatedPrice.total.price)}₽{' '}
                {activeFormat.priceOneTime ? 'за год при оплате сразу' : 'за год'}
              </p>
              <Button
                onClick={() => setIsOpen(true)}
                className='rounded-[16px] duration-200 ease-in-out justify-center bg-accent text-white flex items-center h-[50px] px-[16px] hover:bg-black'>
                Записаться на курс
              </Button>
              <LeadFormDialog
                open={isOpen}
                onOpenChange={setIsOpen}
                program={{
                  productName: program?.name ?? '',
                  productId: program?.productUid ?? null,
                  productCategory: program?.category?.name ?? '',
                  productFaculty: program?.faculty?.name ?? '',
                  institute: program?.institute?.name ?? ''
                }}
              />
            </div>
            {/* что включено */}
            <div
              className={cn(
                'grid grid-cols-1 gap-[8px]',
                'md:grid-cols-2 md:gap-[16px]'
              )}>
              {whatIncludes.map(el => (
                <div
                  key={el.id}
                  className={cn(
                    'flex gap-[12px] bg-[#2A2A2A] p-[16px] rounded-[24px]',
                    'md:p-[24px] md:rounded-[32px]'
                  )}>
                  <div className='mt-[4px]'>{el.icon}</div>
                  <div className='flex flex-col gap-[8px]'>
                    <p className='text-[14px]/[120%] md:text-[20px]/[120%] font-medium'>
                      {el.title}
                    </p>
                    <p className='text-white/60 text-[12px][120%] md:text-[14px]/[120%]'>
                      {el.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {activeFormat?.title === 'Очная форма' ||
          activeFormat?.title === 'Очно-заочная форма' ? (
            <p className='mt-[16px] text-[14px]'>
              *ДОТ –{' '}
              <span className='text-white/60'>
                Дистанционные образовательные технологии
              </span>
            </p>
          ) : null}
        </div>
      </div>
    </section>
  )
}
